import * as React from 'react';
import { RouterProvider as ARIARouterProvider } from 'react-aria-components';

import { useEventCallback } from '@gitbook/util-react-hooks';

// biome-ignore lint/suspicious/noEmptyInterface: We need to support extends here
export interface RouterConfig {}

export type Href = RouterConfig extends { href: infer H } ? H : string;
export type RouterOptions = RouterConfig extends { routerOptions: infer O } ? O : never;

declare module 'react-aria-components' {
    interface RouterConfig {
        href: Href;
        routerOptions: RouterOptions;
    }
}

type Navigate = (href: Href, options?: RouterOptions | undefined) => void;
type UseHref = (href: Href) => string;

type RouterContextType = {
    navigate: Navigate;
    useHref: UseHref;
};

const RouterContext = React.createContext<RouterContextType | null>(null);

export function RouterProvider(props: {
    children: React.ReactNode;
    navigate: Navigate;
    useHref: UseHref;
}) {
    const navigate = useEventCallback(props.navigate);
    const useHref = useEventCallback(props.useHref);
    const value = React.useMemo(() => ({ navigate, useHref }), [navigate, useHref]);
    return (
        <RouterContext.Provider value={value}>
            <ARIARouterProvider useHref={useHref} navigate={navigate}>
                {props.children}
            </ARIARouterProvider>
        </RouterContext.Provider>
    );
}

export function useRouterContext() {
    return React.useContext(RouterContext);
}
